import { apiFetch, type IdResponse, type OperationResult } from "@/lib/apiClient";
import { DEFAULT_PAGE_SIZE, pageQuery, type PageParams } from "@/lib/paging";

export interface TicketTemplate {
  id: string;
  name: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  canDelete: boolean;
}

export interface TicketTemplateList {
  canCreate: boolean;
  canInsert: boolean;
  totalCount: number;
  items: TicketTemplate[];
}

export interface SaveTicketTemplateRequest {
  name: string;
  content: string;
}

interface TicketTemplateResponse {
  id: string;
  name: string;
  content: string | null;
  creationTime: string;
  lastModificationTime: string | null;
  canEdit: boolean;
  canDelete: boolean;
}

interface TicketTemplateListResponse {
  canCreate: boolean;
  canInsert: boolean;
  totalCount: number | string;
  items: TicketTemplateResponse[] | null;
}

function toTicketTemplate(r: TicketTemplateResponse): TicketTemplate {
  return {
    id: r.id,
    name: r.name,
    content: r.content ?? "",
    createdAt: r.creationTime,
    updatedAt: r.lastModificationTime ?? r.creationTime,
    canEdit: r.canEdit === true,
    canDelete: r.canDelete === true,
  };
}

const PATH = "/tickets/templates";

export const ticketTemplates = {
  async list(params: PageParams = {}): Promise<TicketTemplateList> {
    const res = await apiFetch<TicketTemplateListResponse | null>(
      `${PATH}${pageQuery({ pageSize: DEFAULT_PAGE_SIZE, ...params }, { keyword: params.keyword?.trim() })}`,
    );
    return {
      canCreate: res?.canCreate === true,
      canInsert: res?.canInsert === true,
      totalCount: Number(res?.totalCount) || 0,
      items: (res?.items ?? []).map(toTicketTemplate),
    };
  },
  async get(id: string): Promise<TicketTemplate> {
    const r = await apiFetch<TicketTemplateResponse>(`${PATH}/${encodeURIComponent(id)}`);
    return toTicketTemplate(r);
  },
  create(req: SaveTicketTemplateRequest) {
    return apiFetch<IdResponse>(PATH, { method: "POST", body: JSON.stringify(req) });
  },
  update(id: string, req: SaveTicketTemplateRequest) {
    return apiFetch<OperationResult>(`${PATH}/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(req),
    });
  },
  remove(id: string) {
    return apiFetch<OperationResult>(`${PATH}/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};
